// components/MyMessage.tsx
import React from "react";
import { Message } from "../../../types/MessageListType";
import { formatTimeOnly } from "../../../utils/formatUTCtoLocalDate";
import { useTimeFormatOptions } from "../../../hooks/useTimeFormatOptions";
import { LiaCheckDoubleSolid } from "react-icons/lia";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks";
import { setViewImage } from "../../../store/Slices/ViewManagerSlice";
import { FaSquareCheck } from "react-icons/fa6";
import { IoSquareOutline } from "react-icons/io5";
import {
  addMessage,
  removeMessage,
  updateMessageOptions,
} from "../../../store/Slices/MessageOptionsSlice";
import LinkPreview from "../../../components/LinkPreview";
import LinkifiedChatText from "../../../components/LinkifiedChatText";
import { extractFirstUrl, isBareHttpUrl } from "../../../utils/isLink";
import LocationMapEmbed from "../../../components/LocationMapEmbed";
import { useTheme } from "../../../context/ThemeProvider";
import LoadingSkeletonImageDynamic from "../../../components/LoadingSkeletonImageDynamic";
import ChatVideoBubble from "../../../components/ChatVideoBubble";
// @ts-ignore
import VoiceMessagePlayer from "../../../components/VoiceMessagePlayer";
import DocMessageBubble from "../../../components/DocMessageBubble";
import SelectedMessageOption from "./SelectedMessageOption/SelectedMessageOption";
import ShowReplyMessage from "./ReplyMessage/ShowReplyMessage";
import ShowSavedName from "../../../components/ShowSavedName";
import { getRandomColor } from "../../../utils/getRandomColor";
import CallInMessageList from "./CallInMessageList";
import PinIcon from "/MessageListIcons/pin 1.png";
import PinDarkIcon from "/MessageListIcons/pin_dark.png";
import ShowStatusReply from "./ShowStatusReply";
import { RiArrowGoForwardLine } from "react-icons/ri";
import { getMessagePlayUrl, toSameOriginMediaUrl } from "../../../utils/mediaPlayUrl";
import { dimsToMessageSize } from "../../../utils/mediaDims";
import {
  resolveMediaCaption,
  resolveMediaSrc,
} from "../../../utils/mediaMessageUrl";
import { useTranslateText } from "../../../hooks/useTranslateText";
import ThanosSnap from "../../../components/ThanosSnap";
import { finishMessageSnap } from "../../../store/Slices/MessageListSlice";
import { useMapProvider } from "../../../hooks/useMapProvider";
import { isRussianLanguageName } from "../../../utils/mapProvider";

interface MyMessageProps {
  messageData: Message;
}

const MyMessage: React.FC<MyMessageProps> = ({ messageData }) => {
  const dispatch = useAppDispatch();
  const { hour12 } = useTimeFormatOptions();
  const translate = useTranslateText();
  const { languageName } = useMapProvider();
  const editedLabel = (() => {
    const t = translate("edited");
    if (t !== "edited") return t;
    return isRussianLanguageName(languageName) ? "изм." : "edited";
  })();
  const MessageOptions = useAppSelector((state) => state.MessageOptions);
  const snappingIds = useAppSelector(
    (state) => state.MessageList.snappingIds || [],
  );
  const isSnapping = snappingIds.includes(messageData.message_id);
  const userData = useAppSelector((state) => state.userData);
  const currentConversationData = useAppSelector(
    (state) => state.CurrentConversation,
  );
  const myUserId = userData?.user_id;
  const sender = messageData.User;
  const senderId = sender?.user_id ?? messageData.sender_id;
  const isOwn =
    myUserId != null &&
    senderId != null &&
    String(senderId) === String(myUserId);
  const deletedForMe =
    Array.isArray(messageData.deleted_for) &&
    myUserId != null &&
    messageData.deleted_for.some((id) => String(id) === String(myUserId));

  const defaultProps = {
    center: {
      lat: 10.99835602,
      lng: 77.01502627,
    },
    zoom: 11,
  };
  // @ts-ignore
  const { theme } = useTheme();

  const isMessageSelected = MessageOptions.message_list?.Records?.some(
    (message) => message.message_id === messageData.message_id,
  );

  const isCompactChatMedia = location.pathname === "/video-call";

  const mediaCaption = resolveMediaCaption(messageData);
  const mediaSrcRaw = resolveMediaSrc(messageData);
  // Same-origin proxy for chat images (CORS / decode path consistent with video)
  const mediaSrc =
    messageData.message_type === "image" || messageData.message_type === "gif"
      ? toSameOriginMediaUrl(mediaSrcRaw) || mediaSrcRaw
      : mediaSrcRaw;

  const isVisualMedia =
    messageData.message_type === "image" ||
    messageData.message_type === "gif" ||
    messageData.message_type === "video";

  /** Только картинка / GIF / видео без цитаты и без caption — без цветной подложки. */
  const isVisualMediaOnly =
    isVisualMedia && messageData.ParentMessage == null && !mediaCaption;

  /** Медиа + caption в цветном бабле: inset 1px + скругление фото. */
  const isMediaWithCaption =
    isVisualMedia && !!mediaCaption && messageData.ParentMessage == null;

  const isLocationMessage = messageData.message_type === "location";

  const isVoiceMessage =
    messageData.message_type === "voice" ||
    messageData.message_type === "audio";
  const isDocMessage = messageData.message_type === "doc";
  const isCompactBubble = isVoiceMessage || isDocMessage;

  const mediaBubbleCorners = isVisualMediaOnly
    ? "rounded-xl"
    : isMediaWithCaption
      ? "rounded-[7px]"
      : isOwn
        ? "rounded-br-none"
        : "rounded-bl-none";

  /** Одно медиа на бабл — открываем только его (альбомы позже). */
  const openMediaInLightbox = (url: string) => {
    dispatch(
      setViewImage({
        show_image: true,
        image_src: [url],
        currentIndex: 0,
      }),
    );
  };

  /** Превью в чате → лайтбокс с <video> (HLS если COMPLETED). */
  const openVideoInLightbox = () => {
    openMediaInLightbox(getMessagePlayUrl(messageData));
  };

  const handleSelectMessageClick = () => {
    if (MessageOptions.selectMessage) {
      if (isMessageSelected) {
        dispatch(removeMessage(messageData.message_id));
      } else if (isOwn) {
        dispatch(addMessage(messageData));
      } else {
        // Dispatch the first action
        dispatch(
          updateMessageOptions({
            delete_only_from_me: true,
          }),
        );
        // Dispatch the second action
        dispatch(addMessage(messageData));
      }
    }
  };

  // Regex to check if the message contains only emojis
  const isOnlyEmojis = (message: string) => {
    return (
      message &&
      message
        .trim()
        .match(
          /^(?:\p{Emoji_Presentation}|\p{Emoji}\uFE0F|\p{Emoji_Modifier_Base})+$/u,
        )
    );
  };

  const canSelectMessage =
    messageData.message_type !== "call" &&
    !messageData.deleted_for_everyone &&
    !deletedForMe &&
    !isSnapping;

  if ((messageData.deleted_for_everyone || deletedForMe) && !isSnapping) {
    return null;
  }

  return (
    <div
      key={messageData.message_id}
      id={String(messageData.message_id)}
      data-message-id={messageData.message_id}
      className={`relative my-2 flex flex-col rounded-lg ${
        MessageOptions.selectMessage && isMessageSelected
          ? "bg-selectedMessage"
          : ""
      }`}
    >
      {/* Out of flow — must not change bubble width or scroll will jump */}
      {MessageOptions.selectMessage && canSelectMessage ? (
        <button
          type="button"
          aria-label={isMessageSelected ? "Deselect message" : "Select message"}
          onClick={(e) => {
            e.stopPropagation();
            if (isMessageSelected) {
              dispatch(removeMessage(messageData.message_id));
            } else {
              dispatch(addMessage(messageData));
            }
          }}
          className="absolute left-0 top-3 z-[1] h-5 w-5 -translate-x-1 cursor-pointer sm:top-3.5 sm:-translate-x-9"
        >
          <FaSquareCheck
            className={`absolute inset-0 h-5 w-5 text-themeColor transition-opacity duration-150 ${
              isMessageSelected ? "opacity-100" : "opacity-0"
            }`}
          />
          <IoSquareOutline
            className={`absolute inset-0 h-5 w-5 text-[#BDBDBD] transition-opacity duration-150 ${
              isMessageSelected ? "opacity-0" : "opacity-100"
            }`}
          />
        </button>
      ) : null}

      <div className="flex items-end justify-end">
        <div
          onClick={() => {
            if (!canSelectMessage) return;
            handleSelectMessageClick();
          }}
          className={`mx-2 flex w-full flex-col group/forward space-y-2 ${
            MessageOptions.selectMessage ? "cursor-pointer" : ""
          } ${
            isOwn
              ? "order-1 items-end"
              : "order-2"
          }`}
        >
          <div>
            <SelectedMessageOption
              messageData={messageData}
              disabled={
                messageData.message_type == "call" ||
                messageData.deleted_for_everyone ||
                deletedForMe ||
                MessageOptions.selectMessage
              }
            >
            <ThanosSnap
              active={isSnapping}
              onSettled={() =>
                dispatch(finishMessageSnap([messageData.message_id]))
              }
            >
            <div
              className={`${
                isOwn
                  ? `${isOnlyEmojis(messageData.message_content) && messageData.message_type === "text" ? "text-right" : isVisualMediaOnly ? "" : "primary-gradient rounded-br-none"} ml-auto`
                  : `${isOnlyEmojis(messageData.message_content) && messageData.message_type === "text" ? "" : isVisualMediaOnly ? "" : "rounded-bl-none  bg-otherMessageBg"}`
                } group relative flex w-fit min-h-0 ${
                  messageData.ParentMessage != null
                    ? "min-w-[14.5rem] sm:min-w-[17rem]"
                    : "min-w-[1rem]"
                } ${
                  isMediaWithCaption
                    ? "max-w-[20rem] sm:max-w-[22rem]"
                    : location.pathname !== "/video-call"
                      ? "max-w-[17rem] sm:max-w-xl 2xl:max-w-3xl"
                      : "max-w-[17rem] sm:max-w-xl lg:max-w-[17rem]"
                } flex-col ${
                  isVisualMediaOnly
                    ? "overflow-visible rounded-none px-0 py-0"
                    : isMediaWithCaption || isLocationMessage
                      ? "overflow-hidden rounded-lg p-px"
                      : isCompactBubble
                        ? "overflow-visible rounded-lg px-2 py-1.5"
                        : "overflow-visible rounded-lg px-2 py-2"
                } text-sm`}
            >
              {messageData.ParentMessage != null && (
                <ShowReplyMessage
                  reply_to={messageData.reply_to}
                  messageData={messageData.ParentMessage}
                />
              )}
              {currentConversationData.chat_type == "group" &&
                !isOwn &&
                sender && (
                  <div
                    style={{
                      color: getRandomColor(sender.user_name, theme),
                    }}
                    className={`pb-1 text-xs`}
                  >
                    <ShowSavedName
                      user_id={String(sender.user_id)}
                      name={sender.full_name}
                    />
                  </div>
                )}
              <div
                className={`${
                  isVisualMedia ? "flex flex-col" : "flex"
                } w-full min-w-0 overflow-visible`}
              >
                {
                  <>
                    {
                      messageData.message_type == "story_reply" ? (
                        <ShowStatusReply messageData={messageData} />
                      ) : messageData.message_type === "text" ||
                        messageData.message_type === "link" ? (
                        (() => {
                          const previewOn = messageData.link_preview !== false;
                          const hasWebpage = !!(
                            messageData.link_title ||
                            messageData.link_image ||
                            messageData.link_favicon ||
                            messageData.link_site_name
                          );
                          const bareUrl = isBareHttpUrl(
                            messageData.message_content,
                          );
                          const firstUrl =
                            messageData.link_url ||
                            extractFirstUrl(messageData.message_content);
                          const showCard =
                            previewOn && !!(hasWebpage || firstUrl);
                          const showText = !bareUrl || !showCard;
                          return (
                            <div className="w-full min-w-0">
                              {showCard && (
                                <div
                                  className={showText ? "mb-1.5 w-full" : "w-full"}
                                >
                                  <LinkPreview
                                    right={isOwn}
                                    url={
                                      firstUrl ||
                                      messageData.message_content
                                    }
                                    webpage={{
                                      link_url: messageData.link_url,
                                      link_title: messageData.link_title,
                                      link_description:
                                        messageData.link_description,
                                      link_image: messageData.link_image,
                                      link_favicon: messageData.link_favicon,
                                      link_site_name: messageData.link_site_name,
                                    }}
                                  />
                                </div>
                              )}
                              {showText && (
                                <LinkifiedChatText
                                  text={messageData.message_content}
                                  className={`pb-0.5 ${isOnlyEmojis(messageData.message_content) ? "text-4xl" : "pr-5"}`}
                                />
                              )}
                            </div>
                          );
                        })()
                      ) : messageData.message_type === "image" ||
                        messageData.message_type === "gif" ? (
                        <div className="inline-flex w-fit max-w-full flex-col">
                          <div
                            onClick={() => {
                              if (mediaSrc) openMediaInLightbox(mediaSrc);
                            }}
                            className="max-w-full"
                          >
                            <LoadingSkeletonImageDynamic
                              naturalSize
                              naturalSizeCompact={isCompactChatMedia}
                              radius=""
                              className={`${mediaBubbleCorners} cursor-pointer select-none`}
                              image_height=""
                              image_url={mediaSrc}
                              preview_url={
                                messageData.message_preview ||
                                messageData.message_thumbnail ||
                                undefined
                              }
                              blur_hash={
                                messageData.blur_hash ||
                                messageData.blurHash ||
                                undefined
                              }
                              dims={
                                dimsToMessageSize(messageData) ||
                                messageData.message_size ||
                                undefined
                              }
                              image_width=""
                            />
                          </div>
                          {mediaCaption ? (
                            <div
                              className="box-border w-0 min-w-full whitespace-pre-wrap break-words px-1.5 pb-1.5 pt-1 text-sm"
                              style={{ overflowWrap: "anywhere" }}
                            >
                              {mediaCaption}
                            </div>
                          ) : null}
                        </div>
                      ) : messageData.message_type === "video" ? (
                        <div className="inline-flex w-fit max-w-full flex-col">
                          <ChatVideoBubble
                            message={{
                              ...messageData,
                              // Playback via media_url; keep caption in message_content
                              media_url:
                                mediaSrc ||
                                messageData.media_url ||
                                messageData.message_content,
                            }}
                            compact={isCompactChatMedia}
                            withCaption={!!mediaCaption}
                            className={mediaBubbleCorners}
                            onOpen={openVideoInLightbox}
                          />
                          {mediaCaption ? (
                            <div
                              className="box-border w-0 min-w-full whitespace-pre-wrap break-words px-1.5 pb-1.5 pt-1 text-sm"
                              style={{ overflowWrap: "anywhere" }}
                            >
                              {mediaCaption}
                            </div>
                          ) : null}
                        </div>
                      ) : messageData.message_type === "doc" ? (
                        <DocMessageBubble
                          href={mediaSrcRaw}
                          fileNameRaw={messageData.message_length}
                          fileSizeRaw={
                            messageData.file_size_bytes != null &&
                            messageData.file_size_bytes > 0
                              ? String(messageData.file_size_bytes)
                              : messageData.fileSizeBytes != null &&
                                  messageData.fileSizeBytes > 0
                                ? String(messageData.fileSizeBytes)
                                : messageData.message_size
                          }
                          previewUrl={
                            messageData.message_thumbnail ||
                            messageData.message_preview ||
                            null
                          }
                          blurHash={
                            messageData.blur_hash ||
                            messageData.blurHash ||
                            null
                          }
                          isOwn={isOwn}
                          theme={theme}
                        />
                      ) : messageData.message_type === "audio" ||
                        messageData.message_type === "voice" ? (
                        <VoiceMessagePlayer
                          src={messageData.message_content}
                          waveData={
                            messageData.wave_data ||
                            messageData.waveData ||
                            null
                          }
                          durationLabel={messageData.voice_duration || null}
                          isOwn={isOwn}
                          theme={theme}
                        />
                      ) : messageData.message_type === "location" ? (
                        <div
                          className={`w-full max-w-80 overflow-hidden rounded-2xl ${isOwn ? "rounded-br-md bg-themeColor/10" : "rounded-bl-md bg-pdfBg"} min-w-64 p-px ${location.pathname != "/video-call" ? "sm:min-w-80" : "sm:min-w-72"}`}
                        >
                          <LocationMapEmbed
                            content={messageData.message_content}
                            className="h-40 w-full rounded-[7px]"
                          />
                        </div>
                      ) : messageData.message_type === "call" ? (
                        <CallInMessageList messageData={messageData} />
                      ) : (
                        messageData.message_type
                      )
                      // ) : messageData.message_type === "contact" ? (
                      //   <SharedContact messageData={messageData} />
                      // ) : messageData.message_type === "poll" ? (
                      //   <PollMessages messageData={messageData} />
                      // ) :
                      // messageData.message_type
                    }

                  </>
                }
              </div>
              {
                messageData.message_type == "call" ||
                  messageData.deleted_for_everyone ||
                  deletedForMe ? (
                  ""
                ) :
                  !MessageOptions.selectMessage && (
                    <div
                      onClick={() => {
                        dispatch(
                          updateMessageOptions({
                            selectMessage: true,
                            forward_message: true,
                          }),
                        );
                        dispatch(addMessage(messageData));
                      }} className={`absolute ${isOwn ? "-left-10 top-0" : "-right-10 top-0"} invisible group-hover/forward:visible  cursor-pointer flex items-center h-full `}>
                      <div className="bg-secondary rounded-full text-themeColor  p-1">
                        <RiArrowGoForwardLine className="text-lg" />
                      </div>
                    </div>
                  )}
            </div>
            </ThanosSnap>
            </SelectedMessageOption>

            <div
              className={`flex ${isOwn ? "flex-row-reverse" : ""} ${isCompactBubble ? "mt-1" : "mt-2"} items-center gap-x-1`}
            >
              {isOwn && (
                <LiaCheckDoubleSolid
                  title={
                    messageData.message_seen_status == "seen"
                      ? "Seen"
                      : "Unseen"
                  }
                  className={`h-4 w-4 ${messageData.message_seen_status == "seen"
                    ? "text-[#54E0FF]"
                    : "text-gray-400"
                    } `}
                />
              )}
              <div className="text-xs text-lightText">
                {messageData.is_edited ? (
                  <span className="mr-1 opacity-80">{editedLabel}</span>
                ) : null}
                {formatTimeOnly(messageData.createdAt, { hour12 })}
              </div>
              {messageData.starred && (

                <div
                  className="theme-colored-icon h-3 w-3"
                  style={{
                    maskImage: `url('/Home/Star - Iconly Pro.png')`,
                    WebkitMaskImage: `url('/Home/Star - Iconly Pro.png')`,
                  }}
                />
              )}
              {messageData.pinned && (
                <>
                  {/* <img
                    className="h-4 w-4 object-contain "
                    src={theme == "dark" ? PinDarkIcon : PinIcon}
                    alt=""
                  /> */}
                  <div
                    className="theme-colored-icon h-4 w-4"
                    style={{
                      maskImage: `url('${theme == "dark" ? PinDarkIcon : PinIcon}')`,
                      WebkitMaskImage: `url('${theme == "dark" ? PinDarkIcon : PinIcon}')`,
                    }}
                  />
                </>
              )}
            </div>
          </div>
        </div>

        <img
          src={sender?.profile_pic || ""}
          alt="My profile"
          className={`h-7 w-7 rounded-full object-cover ${isOwn
            ? "order-2"
            : "order-1"
            } mt-auto ${["video", "image"].includes(messageData.message_type!)
              ? "mb-7"
              : isCompactBubble
                ? "mb-3"
                : "mb-5"
            }`}
        />
      </div>
    </div>
  );
};

export default MyMessage;
